import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, publicFileUrl } from "@/lib/api";
import { formatRp } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HeartHandshake, Sprout, CheckCircle2, Share2, Copy } from "lucide-react";
import PaymentSection from "@/components/PaymentSection";

const PRESETS = [50000, 100000, 250000, 500000, 1000000];

export default function AlumniPage() {
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, se] = await Promise.all([api.get("/public/summary"), api.get("/public/settings")]);
        setSummary(s.data); setSettings(se.data);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(amount);
    if (!amt || amt < 1000) return toast.error("Nominal minimal Rp1.000");
    setLoading(true);
    try {
      await api.post("/public/alumni-pledge", {
        name: name.trim(), contact: contact.trim(), amount: amt, note: note.trim(),
      });
      setDone(true);
      toast.success("Terima kasih! Pledge terkirim ke bendahara.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim");
    } finally { setLoading(false); }
  };

  const share = () => {
    const url = `${window.location.origin}/infaq/alumni`;
    const text = `Dukung ${settings?.name || "Infak Khairul Falah"} — ruang baca & kebun belajar untuk anak Madrasah Khairul Falah. Daftar kontribusi: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/infaq/alumni`);
      toast.success("Tautan tersalin");
    } catch (e) { toast.error("Gagal menyalin"); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/infaq" className="inline-flex items-center gap-2 mb-6 text-[#1B4D3E] hover:underline" data-testid="alumni-back-link">
          ← Kembali ke laporan publik
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#1B4D3E] text-[#C59B27] mb-4">
            {settings?.logo_file_id ? (
              <img src={publicFileUrl(settings.logo_file_id)} alt="Logo" className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <HeartHandshake size={28} />
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1B4D3E]">Infak Alumni & Donatur</h1>
          <p className="text-[#64748B] mt-2 max-w-xl mx-auto">
            {settings?.tagline || "Bersama menghadirkan ruang baca dan kebun belajar untuk anak-anak."}
          </p>
          {summary && (
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
              <span className="px-3 py-1 rounded-full bg-[#EAF2EF] text-[#1B4D3E]" data-testid="alumni-progress">
                Terkumpul {formatRp(summary.total_received)} dari {formatRp(summary.target_amount)} ({(summary.progress_pct || 0).toFixed(1)}%)
              </span>
            </div>
          )}
        </div>

        {done ? (
          <Card className="p-8 text-center border-emerald-200 bg-emerald-50" data-testid="alumni-done-card">
            <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
            <h2 className="text-2xl font-bold text-emerald-900 mb-2">Jazakumullah Khairan Katsira</h2>
            <p className="text-emerald-800 mb-5">
              Niat baik Anda sudah dicatat. Bendahara akan segera menghubungi atau Anda bisa langsung transfer sesuai petunjuk di bawah.
            </p>
            {settings && (
              <div className="text-left bg-white p-4 rounded-lg border border-emerald-200 mb-4">
                <PaymentSection settings={settings} />
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => setDone(false)} variant="outline" data-testid="alumni-new-btn">Catat pledge lagi</Button>
              <Button onClick={share} className="bg-[#25D366] hover:bg-[#1EBE57] text-white" data-testid="alumni-share-btn">
                <Share2 size={14} className="mr-2" /> Ajak teman via WhatsApp
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <Card className="p-6 sm:p-8 mb-4 border-[#E2E8F0]">
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="text-base font-semibold text-[#1B4D3E]">Pilih Nominal</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                    {PRESETS.map((v) => (
                      <button type="button" key={v}
                        onClick={() => setAmount(String(v))}
                        className={`py-3 px-3 rounded-lg text-sm font-semibold border transition
                          ${parseInt(amount) === v
                            ? "bg-[#1B4D3E] text-white border-[#1B4D3E]"
                            : "bg-white text-[#1B4D3E] border-[#E2E8F0] hover:border-[#1B4D3E]"}`}
                        data-testid={`alumni-preset-${v}`}
                      >
                        {formatRp(v)}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Input type="number" min="1000" value={amount} onChange={(e) => setAmount(e.target.value)}
                      placeholder="Atau isi nominal lain (minimal Rp1.000)" required data-testid="alumni-amount-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Nama (opsional)</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Boleh dikosongkan jika anonim" data-testid="alumni-name-input" />
                  </div>
                  <div>
                    <Label>Kontak (opsional)</Label>
                    <Input value={contact} onChange={(e) => setContact(e.target.value)}
                      placeholder="WA / Email untuk konfirmasi" data-testid="alumni-contact-input" />
                  </div>
                </div>

                <div>
                  <Label>Pesan untuk pengurus (opsional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Doa, pesan singkat, atau catatan khusus" data-testid="alumni-note-input" />
                </div>

                <Button type="submit" disabled={loading} size="lg"
                  className="w-full bg-[#1B4D3E] hover:bg-[#143E32] text-white" data-testid="alumni-submit-btn">
                  <HeartHandshake size={16} className="mr-2" /> {loading ? "Memproses..." : "Kirim Pledge"}
                </Button>
                <p className="text-xs text-[#94A3B8] text-center">
                  Dengan mengirim, Anda mencatatkan niat kontribusi. Bendahara akan menindaklanjuti dan uang masuk saldo setelah dikonfirmasi.
                </p>
              </form>
            </Card>

            {settings?.how_to_contribute && (
              <Card className="p-5 bg-[#FBF5E8] border-[#C59B27]/30 mb-4">
                <div className="font-semibold text-[#1B4D3E] mb-2 flex items-center gap-2">
                  <Sprout size={16} /> Cara Menitipkan Infak
                </div>
                <p className="text-sm text-[#64748B] whitespace-pre-wrap">{settings.how_to_contribute}</p>
              </Card>
            )}

            {/* Payment methods: QRIS + bank + WhatsApp confirmation */}
            {settings && (
              <Card className="p-5 mb-4 border-[#E2E8F0]" data-testid="alumni-payment-card">
                <PaymentSection settings={settings} />
              </Card>
            )}

            <div className="flex flex-wrap gap-2 justify-center mt-6">
              <Button variant="outline" onClick={copyLink} data-testid="alumni-copy-link-btn">
                <Copy size={14} className="mr-2" /> Salin tautan halaman
              </Button>
              <Button onClick={share} className="bg-[#25D366] hover:bg-[#1EBE57] text-white" data-testid="alumni-share-wa-btn">
                <Share2 size={14} className="mr-2" /> Bagikan via WhatsApp
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
